/**
 * Query keys are composed by a function, never written twice as a literal.
 *
 * A key spelled at the point of use and again at the point of invalidation is
 * the classic way a list stops refreshing after a create: both look right, one
 * of them is `["sites"]` and the other `["sites", workspaceId]`, and nothing
 * ever fails — the screen is simply stale (the fleet standard DevGuru wrote up
 * in `kb/generic/admin-console-ui.md`).
 */
export const keys = {
  /** Who is signed in. Invalidated by signing in and by signing out. */
  me: () => ["me"] as const,

  /**
   * The sites of one workspace — the list the console shows, and the list a
   * create or an archive has to invalidate.
   *
   * The workspace is *in* the key and not only in the path it fetches: two
   * workspaces are two lists, and a key that dropped the distinction would
   * hand one of them the other's sites after a create. This is the example the
   * comment above is about, so it is written the way that comment prescribes.
   */
  sites: (workspaceId: string) => ["sites", workspaceId] as const,

  /** One site by id, addressed as `/v1/sites/:site` does — with no workspace. */
  site: (siteId: string) => ["site", siteId] as const,
  siteActivity: (siteId: string) => ["site-activity", siteId] as const,

  /**
   * The API tokens of one workspace.
   *
   * The workspace is in the key for the reason `sites` gives, and here it is
   * load-bearing twice over: a key grants access to exactly one workspace
   * (design note 0003), so a list handed to the wrong one would be showing
   * credentials that have nothing to do with the screen somebody is reading.
   *
   * There is no key for a single token on purpose. The value exists once, in
   * the answer to the create, and is never fetched again — a cached entry for
   * one token would be a place for it to end up.
   */
  tokens: (workspaceId: string) => ["tokens", workspaceId] as const,

  /**
   * The applications this **account** has connected.
   *
   * No workspace in the key, and that is the one thing worth saying about it: a
   * grant is held by a person, so there is one list per signed-in account and a
   * workspace here would split a single truth into copies that disagree. It is
   * invalidated by disconnecting, and by nothing else — signing out drops the
   * whole cache with the session.
   */
  connectedApplications: () => ["connected-applications"] as const,

  /**
   * The members of one workspace. In the key for the reason `tokens` gives:
   * membership is a fact about one workspace, and a list handed to the wrong
   * one would show who belongs where they do not.
   */
  members: (workspaceId: string) => ["members", workspaceId] as const,

  /** This workspace's pending invites — sent, not accepted, not revoked. */
  invites: (workspaceId: string) => ["invites", workspaceId] as const,

  /**
   * The signed-in account's own open invites, no workspace in the key —
   * `/v1/invites/mine` is addressed by the caller's e-mail, not a workspace
   * in the path, for the reason `connectedApplications` gives: this is a
   * fact about the account, not about any one workspace.
   */
  myInvites: () => ["my-invites"] as const,
};
